'use client'

import React, { createContext, useContext, useState } from 'react'

type DemoState = {
  isOpen: boolean
  scope: string | null
  scriptId: string | null
  sceneIndex: number
}

type DemoContextType = {
  state: DemoState
  startDemo: (scope: string, scriptId: string) => void
  next: () => void
  prev: () => void
  close: () => void
}

const DemoContext = createContext<DemoContextType | null>(null)

export function DemoProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<DemoState>({
    isOpen: false,
    scope: null,
    scriptId: null,
    sceneIndex: 0,
  })

  function startDemo(scope: string, scriptId: string) {
    setState({
      isOpen: true,
      scope,
      scriptId,
      sceneIndex: 0,
    })
  }

  function next() {
    setState((s) => ({ ...s, sceneIndex: s.sceneIndex + 1 }))
  }

  function prev() {
    setState((s) => ({ ...s, sceneIndex: Math.max(0, s.sceneIndex - 1) }))
  }

  function close() {
    setState({
      isOpen: false,
      scope: null,
      scriptId: null,
      sceneIndex: 0,
    })
  }

  return (
    <DemoContext.Provider value={{ state, startDemo, next, prev, close }}>
      {children}
    </DemoContext.Provider>
  )
}

export function useDemo() {
  const ctx = useContext(DemoContext)
  if (!ctx) throw new Error('useDemo must be used within DemoProvider')
  return ctx
}